/**
 * Stage Transfer (Feb 2026)
 *
 * Queue of "Produced but not yet transferred" lots at every WIP floor.
 * Warehouse staff clicks Approve to ship a lot to the next stage godown.
 * A settings toggle at the top lets Admin flip the whole plant into
 * Auto-Transfer mode (produced qty flows immediately without a click).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useRbac } from "@/lib/rbac";
import { reversalEligibility, isReversed } from "@/lib/stageReversal";
import { ReverseTransferDialog } from "@/components/ReverseTransferDialog";
import {
  TruckIcon,
  ArrowsClockwiseIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ArrowRightIcon,
  ToggleLeftIcon,
  ToggleRightIcon,
  TrashIcon,
  ArrowCounterClockwiseIcon,
} from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export default function StageTransfer() {
  const { can: rbacCan } = useRbac();
  const canReverse = rbacCan("stage_transfer", "reverse");
  const [revTarget, setRevTarget] = useState(null);
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [autoOn, setAutoOn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [qtyOverride, setQtyOverride] = useState({});
  // Feb 25 · Mandatory Batch Selection · per-row lots cache + picked lot.
  const [lotsByKey, setLotsByKey] = useState({});     // key → [lots] | null (not fetched) | []
  const [lotByKey, setLotByKey] = useState({});       // key → lot_id
  const [lotsLoading, setLotsLoading] = useState({}); // key → bool

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: h }, { data: s }] = await Promise.all([
        api.get("/stage-transfers/pending"),
        api.get("/stage-transfers?limit=20"),
        api.get("/settings/stage-auto-transfer"),
      ]);
      setRows(p || []);
      setHistory(h || []);
      setAutoOn(!!s?.enabled);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load stage transfers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleAuto = async () => {
    try {
      const { data } = await api.put("/settings/stage-auto-transfer", { enabled: !autoOn });
      setAutoOn(!!data.enabled);
      toast.success(`Auto-Transfer ${data.enabled ? "ON" : "OFF"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to toggle");
    }
  };

  const approve = async (r) => {
    const key = `${r.godown_id}::${r.rm_code}`;
    // Feb 27, 2026 · No implicit pending-qty default. If the operator
    // never touched the input we treat it as an unfilled entry and
    // block the post so it's obvious a number is required.
    const raw = qtyOverride[key];
    const qty = Number(raw) || 0;
    if (!raw || qty <= 0) {
      toast.error(`Enter the qty to transfer for ${r.rm_code} — nothing is pre-filled`);
      return;
    }
    if (qty <= 0) { toast.error("Enter a qty > 0"); return; }
    if (qty - r.pending_qty > 1e-4) { toast.error(`Only ${fmt(r.pending_qty)} ${r.uom} available`); return; }
    if (!r.next_godown_id) { toast.error(`No next-stage godown mapped for ${r.department || r.godown_name}`); return; }
    // Feb 25 · Mandatory Batch Selection · when the source has lot
    // records, the operator MUST pick one before approving. WIP-to-WIP
    // moves where the source has no lots proceed as aggregate (production
    // output isn't lot-materialized yet — inline warning explains this).
    const lots = lotsByKey[key];
    const chosen = lotByKey[key] || "";
    if (Array.isArray(lots) && lots.length > 0 && !chosen) {
      toast.error("Batch selection is mandatory. Please select specific batches to proceed.");
      return;
    }
    setBusyId(key);
    try {
      await api.post("/stage-transfers", {
        rm_code: r.rm_code,
        qty,
        from_godown_id: r.godown_id,
        to_godown_id: r.next_godown_id,
        uom: r.uom,
        lot_id: chosen,
        remarks: `Stage transfer · ${r.department} → ${r.next_godown_name}`,
      });
      toast.success(`${fmt(qty)} ${r.uom} of ${r.rm_code} moved to ${r.next_godown_name}`);
      setQtyOverride((m) => ({ ...m, [key]: undefined }));
      setLotByKey((m) => ({ ...m, [key]: "" }));
      // Refresh cached lots so the picker reflects the new balance.
      setLotsByKey((m) => ({ ...m, [key]: null }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally {
      setBusyId("");
    }
  };

  // Lazy-fetch lots at the source WIP godown for a given row. Called
  // when the picker's `onFocus` fires — avoids N+1 requests on page load.
  const fetchLots = async (r) => {
    const key = `${r.godown_id}::${r.rm_code}`;
    if (lotsByKey[key] !== undefined && lotsByKey[key] !== null) return;
    setLotsLoading((m) => ({ ...m, [key]: true }));
    try {
      const { data } = await api.get("/inventory/available-lots", {
        params: { rm_code: r.rm_code, godown_code: r.godown_code || "" },
      });
      const sorted = (data || []).slice().sort((a, b) => (a.received_date || "").localeCompare(b.received_date || ""));
      setLotsByKey((m) => ({ ...m, [key]: sorted }));
    } catch (e) {
      setLotsByKey((m) => ({ ...m, [key]: [] }));
    } finally {
      setLotsLoading((m) => ({ ...m, [key]: false }));
    }
  };

  const departments = useMemo(
    () => Array.from(new Set(rows.map((r) => r.department).filter(Boolean))).sort(),
    [rows],
  );
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (dept === "all" || r.department === dept) &&
        (!needle ||
          (r.rm_code || "").toLowerCase().includes(needle) ||
          (r.godown_name || "").toLowerCase().includes(needle)),
    );
  }, [rows, q, dept]);

  const inpCls =
    "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono focus:outline-none focus:border-[var(--accent)]";

  return (
    <div className="p-6 space-y-5" data-testid="stage-transfer-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <TruckIcon size={11} weight="bold" />
            Production &amp; Planning · Stage Transfer
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">
            Stage Transfer
          </h1>
          <div className="text-xs text-zinc-500 mt-1">
            Ship produced qty from one department floor to the next. Every approved transfer writes an
            OUT leg at the source and an IN leg at the destination.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="stage-auto-toggle"
            onClick={toggleAuto}
            className={`h-10 px-4 border rounded-sm text-sm flex items-center gap-2 ${
              autoOn
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                : "border-[var(--border)] hover:bg-[var(--bg-2)] text-white"
            }`}
            title="Auto-transfer produced qty into the next stage's godown immediately (skips this queue)."
          >
            {autoOn ? <ToggleRightIcon size={16} weight="fill" /> : <ToggleLeftIcon size={16} weight="bold" />}
            Auto-Transfer: {autoOn ? "ON" : "OFF"}
          </button>
          <button
            data-testid="stage-refresh"
            onClick={load}
            disabled={loading}
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2"
          >
            <ArrowsClockwiseIcon size={13} weight="bold" />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        <span className="text-zinc-500 font-mono uppercase tracking-widest">Department:</span>
        {["all", ...departments].map((d) => (
          <button
            key={d}
            data-testid={`stage-dept-${d}`}
            onClick={() => setDept(d)}
            className={`px-2.5 py-1 rounded-sm border font-mono uppercase tracking-widest ${
              dept === d
                ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white"
                : "border-[var(--border)] text-zinc-500 hover:text-white"
            }`}
          >
            {d === "all" ? "All" : d}
          </button>
        ))}
        <div className="ml-auto relative min-w-[220px]">
          <MagnifyingGlassIcon size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="stage-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search material or floor…"
            className="w-full h-8 pl-8 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
          />
        </div>
      </div>

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="stage-pending-table">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-4 py-3">From Floor</th>
              <th className="text-left px-4 py-3">Material</th>
              <th className="text-right px-4 py-3">Produced</th>
              <th className="text-right px-4 py-3">Transferred</th>
              <th className="text-right px-4 py-3">Pending</th>
              <th className="text-left px-4 py-3">→ Next</th>
              <th className="text-left px-4 py-3">Batch / Lot</th>
              <th className="text-right px-4 py-3">Qty to Transfer</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const key = `${r.godown_id}::${r.rm_code}`;
              // Feb 27, 2026 · No auto-prefill — operator must type the
              // qty consciously to avoid accidental full-transfer posts.
              // Empty string on first render; internal `qty` computation
              // still falls back to pending when the operator leaves it
              // blank AND clicks Approve without editing.
              const qty = qtyOverride[key] ?? "";
              const lots = lotsByKey[key];
              const loadingLots = !!lotsLoading[key];
              const noLots = Array.isArray(lots) && lots.length === 0;
              const mustPickBatch = Array.isArray(lots) && lots.length > 0 && !lotByKey[key];
              return (
                <tr key={key} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`stage-row-${i}`}>
                  <td className="px-4 py-3 text-zinc-300">{r.godown_name}</td>
                  <td className="px-4 py-3 font-mono text-[var(--accent)]">{r.rm_code}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-sky-300">{fmt(r.produced)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-zinc-500">{fmt(r.transferred_out)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-300">{fmt(r.pending_qty)}</td>
                  <td className="px-4 py-3 text-zinc-300">
                    {r.next_godown_name ? (
                      <span className="inline-flex items-center gap-1"><ArrowRightIcon size={11} weight="bold" /> {r.next_godown_name}</span>
                    ) : (
                      <span className="text-red-400 text-xs">no next stage mapped</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`stage-batch-${i}`}
                      value={lotByKey[key] || ""}
                      onFocus={() => fetchLots(r)}
                      onChange={(e) => setLotByKey((m) => ({ ...m, [key]: e.target.value }))}
                      className={`h-8 px-2 text-xs bg-[var(--bg-0)] border rounded-sm font-mono ${mustPickBatch ? "border-amber-500/50 text-amber-200" : "border-[var(--border)] text-zinc-300"}`}
                    >
                      <option value="">
                        {loadingLots ? "Loading…" : (lots === undefined ? "Click to load…" : (noLots ? "Aggregate (no lots)" : "Select batch…"))}
                      </option>
                      {(lots || []).map((l) => (
                        <option key={l.lot_id} value={l.lot_id}>
                          {l.batch_no} · {l.received_date} · {fmt(l.available_qty)}
                        </option>
                      ))}
                    </select>
                    {noLots && (
                      <div data-testid={`stage-aggregate-${i}`} className="text-[9px] text-amber-300/80 font-mono mt-0.5">
                        Aggregate transfer · WIP source has no lot records
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input
                      data-testid={`stage-qty-${i}`}
                      type="number"
                      step="0.001"
                      value={qty}
                      placeholder={`enter ≤ ${fmt(r.pending_qty)}`}
                      onChange={(e) => setQtyOverride((m) => ({ ...m, [key]: e.target.value }))}
                      className={`${inpCls} text-right w-28 ml-auto`}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`stage-approve-${i}`}
                      onClick={() => approve(r)}
                      disabled={busyId === key || !r.next_godown_id || mustPickBatch}
                      title={mustPickBatch ? "Batch selection is mandatory. Please select specific batches to proceed." : ""}
                      className="h-8 px-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs rounded-sm inline-flex items-center gap-1.5"
                    >
                      <CheckCircleIcon size={13} weight="bold" />
                      Approve
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="text-center py-16 text-zinc-500 text-xs">
                  {autoOn
                    ? "Auto-Transfer is ON — nothing waits in the queue."
                    : "No produced qty is pending transfer right now."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {history.length > 0 && (
        <div className="tactical-card" data-testid="stage-history">
          <div className="px-4 py-3 border-b border-[var(--border)] text-[10px] uppercase tracking-widest text-zinc-500">
            Recent Transfers
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Material</th>
                <th className="text-left px-4 py-3">From</th>
                <th className="text-left px-4 py-3">Remarks</th>
                <th className="text-right px-4 py-3">Qty</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => {
                const elig = reversalEligibility(h, canReverse);
                return (
                <tr key={h.id} className="border-b border-[var(--border)]" data-testid={`stage-hist-${i}`}>
                  <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{(h.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 font-mono text-[var(--accent)]">{h.rm_code}</td>
                  <td className="px-4 py-3 text-zinc-300">{h.godown_name}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{h.remarks}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-white">{fmt(h.qty)} {h.uom}</td>
                  <td className="px-4 py-3 text-xs" data-testid={`stage-hist-status-${i}`}>
                    {isReversed(h) ? (
                      <div>
                        <span className="px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 uppercase tracking-widest text-[10px]">Reversed</span>
                        <div className="text-zinc-500 mt-1" data-testid={`stage-hist-void-info-${i}`}>
                          {(h.voided_at || "").slice(0, 16).replace("T", " ")} · {h.voided_by} · {h.void_reason}
                        </div>
                      </div>
                    ) : elig.code === "historical" ? (
                      <span className="text-zinc-400" data-testid={`stage-hist-review-${i}`}>Needs Review — Historical Transfer</span>
                    ) : (
                      <span className="text-zinc-400">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!isReversed(h) && (
                    <button
                      data-testid={`stage-hist-del-${i}`}
                      onClick={() => { if (elig.ok) setRevTarget(h); }}
                      disabled={!elig.ok}
                      className="p-1.5 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={elig.label}
                    >
                      <ArrowCounterClockwiseIcon size={13} weight="bold" />
                    </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {revTarget && (
        <ReverseTransferDialog transfer={revTarget} onClose={() => setRevTarget(null)}
          onDone={() => { setRevTarget(null); load(); }} />
      )}
    </div>
  );
}
